import React from 'react';
import { Box, InputAdornment } from '@mui/material';
import { InfoTooltip } from '../../components/common/InfoTooltip';
import { FormattedNumberTextField } from '../../components/common/FormattedNumberTextField';
import type { GlobalRuntimeSettings } from '../../services/globalSettings.service';
import type { NumberSettingField } from './global-settings.config';
import { numberFieldLabels } from './global-settings.config';

interface GlobalSettingsNumberGridProps {
  config: GlobalRuntimeSettings;
  fields: NumberSettingField[];
  columns?: 2 | 3;
  inputProps?: {
    min?: number;
    max?: number;
    step?: number;
  };
  /** Show each field's description in an info-icon tooltip instead of helper text under the input. */
  descriptionAsTooltip?: boolean;
  integer?: boolean;
  integerForField?: (field: NumberSettingField) => boolean;
  errorForField?: (field: NumberSettingField) => boolean;
  onNumberChange: (field: NumberSettingField, value: number | undefined) => void;
}

export const GlobalSettingsNumberGrid: React.FC<GlobalSettingsNumberGridProps> = ({
  config,
  fields,
  columns = 2,
  inputProps = { min: 1, step: 1 },
  descriptionAsTooltip = false,
  integer = true,
  integerForField,
  errorForField,
  onNumberChange,
}) => (
  <Box className={`grid grid-cols-1 gap-5 ${columns === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
    {fields.map((field) => (
      <FormattedNumberTextField
        key={field}
        label={numberFieldLabels[field].label}
        helperText={descriptionAsTooltip ? undefined : numberFieldLabels[field].helperText}
        InputProps={
          descriptionAsTooltip
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <InfoTooltip
                      description={numberFieldLabels[field].helperText}
                      label={`About ${numberFieldLabels[field].label}`}
                    />
                  </InputAdornment>
                ),
              }
            : undefined
        }
        integer={integerForField ? integerForField(field) : integer}
        value={config[field]}
        onValueChange={(value) => onNumberChange(field, value)}
        error={errorForField ? errorForField(field) : config[field] <= 0}
        fullWidth
        inputProps={inputProps}
      />
    ))}
  </Box>
);
